import React, { useState, useEffect } from 'react';
import { Card, CardBody, CardHeader, Row, Col, Form, FormGroup, Label, Input, Button } from 'reactstrap';
import { MapContainer, TileLayer, Marker, Circle, useMapEvents } from 'react-leaflet';
import AsyncSelect from 'react-select/async';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix leaflet default icon issue
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import { Breadcrumbs, H3, H5 } from '../../../AbstractElements';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { getClientUrlDataToken, postClientUrlWithToken } from '../../../Utils/restUtils';
import Transparentspinner from '../../Common/Loader/Transparentspinner';
import HandleApiError from '../../Common/ErrorImport/ErrorImport';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});
L.Marker.prototype.options.icon = DefaultIcon;

const LocationPicker = ({ position, setPosition, fetchReverseGeocode }) => {
  useMapEvents({
    click(e) {
      const { lat, lng } = e.latlng;
      setPosition({ lat, lng });
      fetchReverseGeocode(lat, lng);
    },
  });
  return position === null ? null : (
    <Marker position={position}></Marker>
  );
};

const UpdateMapCenter = ({ position }) => {
  const map = useMapEvents({});
  useEffect(() => {
    if (position && !isNaN(position.lat) && !isNaN(position.lng)) {
      map.setView([position.lat, position.lng], map.getZoom(), {
        animate: true,
      });
    }
  }, [position.lat, position.lng, map]);
  return null;
};

function AddEditGeoZone() {
  const [searchParams] = useSearchParams();
  const locationId = searchParams.get("id");
  const isEdit = !!locationId;
  const navigate = useNavigate();
  const authState = useSelector((state) => state.auth);
  const [loading, setLoading] = useState(false);

  const [zoneName, setZoneName] = useState('');
  const [placeName, setPlaceName] = useState(null);
  const [position, setPosition] = useState({ lat: 11.2588, lng: 75.7804 }); // Default Kozhikode
  const [radius, setRadius] = useState(150); // Default radius in meters

  const timeoutRef = React.useRef(null);

  useEffect(() => {
    if (isEdit) {
      fetchGeoZoneData();
    }
  }, [isEdit]);

  const fetchGeoZoneData = async () => {
    setLoading(true);
    try {
      const response = await getClientUrlDataToken(
        authState.apiUrl,
        authState.userRole === 1
          ? `/admin/show-geofence-location?location_id=${locationId}`
          : `/show-geofence-location?location_id=${locationId}`
      );
      if (response.status === true) {
        const geoData = Array.isArray(response.data.data)
          ? response.data.data[0]
          : response.data.data;

        if (geoData) {
          setZoneName(geoData.name || '');
          const lat = parseFloat(geoData.latitude);
          const lng = parseFloat(geoData.longitude);
          setPosition({ lat, lng });
          setRadius(parseInt(geoData.radius, 10) || 150);
          setPlaceName({
            label: geoData.place || '',
            value: { lat, lng }
          });
        }
      } else {
        toast.error(response.data?.message || "Failed to load geozone details");
      }
    } catch (error) {
      console.error("Error loading geozone data", error);
      toast.error("An error occurred while loading geozone details");
    }
    setLoading(false);
  };

  // Debounce helper for AsyncSelect
  const loadOptions = (inputValue, callback) => {
    if (!inputValue || inputValue.length < 3) {
      callback([]);
      return;
    }

    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = setTimeout(() => {
      fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${inputValue}&limit=20&accept-language=en`)
        .then((res) => {
          if (!res.ok) throw new Error("API returned status: " + res.status);
          return res.json();
        })
        .then((data) => {
          const options = data.map((item) => ({
            label: item.display_name,
            value: { lat: parseFloat(item.lat), lng: parseFloat(item.lon) }
          }));
          callback(options);
        })
        .catch((error) => {
          console.error("Error fetching places", error);
          callback([]);
        });
    }, 1000); // 1-second debounce delay
  };

  const fetchReverseGeocode = async (lat, lng) => {
    try {
      const response = await fetch(`https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}`);
      const data = await response.json();
      if (data && data.display_name) {
        setPlaceName({ label: data.display_name, value: { lat, lng } });
      } else {
        setPlaceName({ label: `Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}`, value: { lat, lng } });
      }
    } catch (error) {
      console.error("Error reverse geocoding", error);
      setPlaceName({ label: `Lat: ${lat.toFixed(4)}, Lng: ${lng.toFixed(4)}`, value: { lat, lng } });
    }
  };

  const handlePlaceSelect = (selectedOption) => {
    setPlaceName(selectedOption);
    if (selectedOption) {
      setPosition(selectedOption.value);
    }
  };

  const handleGetCurrentLocation = () => {
    if ("geolocation" in navigator) {
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setPosition({ lat, lng });
          fetchReverseGeocode(lat, lng);
        },
        (err) => {
          alert("Error getting location: " + err.message);
        }
      );
    } else {
      alert("Geolocation is not supported by this browser.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!zoneName || !placeName || !position.lat || !position.lng || !radius) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    const postRequest = {
      name: zoneName,
      place: placeName?.label || '',
      latitude: position.lat,
      longitude: position.lng,
      radius: radius
    };

    if (isEdit) {
      postRequest.location_id = locationId;
    }

    const endpoint = isEdit
      ? (authState.userRole === 1 ? "/admin/update-geofence-location" : "/update-geofence-location")
      : (authState.userRole === 1 ? "/admin/store-geofence-location" : "/store-geofence-location");

    try {
      const response = await postClientUrlWithToken(
        authState.apiUrl,
        endpoint,
        postRequest
      );

      if (response.status === 200) {
        if (response.data.status === true) {
          toast.success(response.data.message || (isEdit ? "Geozone updated successfully" : "Geozone added successfully"));
          navigate(`${process.env.PUBLIC_URL}/attendance/geofencing`);
        } else {
          if (Array.isArray(response.data.message)) {
            response.data.message.forEach((err) => {
              toast.error(err);
            });
          } else {
            toast.error(response.data.message || "An error occurred");
          }
        }
      } else {
        HandleApiError(navigate, response.status);
      }
    } catch (error) {
      console.error("Submit error", error);
      toast.error("An error occurred while saving geozone details");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Transparentspinner />}
      <Breadcrumbs mainTitle={isEdit ? 'Edit Geo Zone' : 'Add Geo Zone'} parent="Attendance" title={isEdit ? 'Edit Geo Zone' : 'Add Geo Zone'} />

      <Card>
        <CardHeader>
          <div className="d-flex justify-content-between align-items-center">
            <H3 className="mb-0">{isEdit ? 'Edit Geo Zone' : 'Add Geo Zone'}</H3 >
            <button onClick={handleGetCurrentLocation} className="btn btn-primary">
              <i className="fa fa-map-marker me-1"></i> Get Current Location
            </button>
          </div>
        </CardHeader>
        <CardBody>
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col md="5">
                <FormGroup>
                  <Label>Zone Name <span className="text-danger">*</span></Label>
                  <Input
                    type="text"
                    placeholder="e.g. BillerQ HQ"
                    value={zoneName}
                    onChange={(e) => setZoneName(e.target.value)}
                    required
                  />
                </FormGroup>

                <FormGroup>
                  <Label>Place Name <span className="text-danger">*</span></Label>
                  <AsyncSelect
                    cacheOptions
                    loadOptions={loadOptions}
                    value={placeName}
                    onChange={handlePlaceSelect}
                    placeholder="Search for a place..."
                    isClearable
                    required={!placeName}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: '#ced4da',
                        minHeight: '38px',
                      })
                    }}
                  />
                </FormGroup>

                <Row>
                  <Col md="6">
                    <FormGroup>
                      <Label>Latitude <span className="text-danger">*</span></Label>
                      <Input
                        type="number"
                        step="any"
                        value={position.lat}
                        onChange={(e) => {
                          const lat = parseFloat(e.target.value) || 0;
                          setPosition({ ...position, lat });
                        }}
                        required
                      />
                    </FormGroup>
                  </Col>
                  <Col md="6">
                    <FormGroup>
                      <Label>Longitude <span className="text-danger">*</span></Label>
                      <Input
                        type="number"
                        step="any"
                        value={position.lng}
                        onChange={(e) => {
                          const lng = parseFloat(e.target.value) || 0;
                          setPosition({ ...position, lng });
                        }}
                        required
                      />
                    </FormGroup>
                  </Col>
                </Row>

                <FormGroup>
                  <Label>Radius (meters) <span className="text-danger">*</span></Label>
                  <Input
                    type="number"
                    min="1"
                    value={radius}
                    onChange={(e) => setRadius(parseInt(e.target.value) || 0)}
                    required
                  />
                </FormGroup>

                <div className="mt-4">
                  <Button color="primary" type="submit" className="me-2">Save Geo Zone</Button>
                  <Button color="light" type="button" onClick={() => navigate(`${process.env.PUBLIC_URL}/attendance/geofencing`)}>Cancel</Button>
                </div>
              </Col>

              <Col md="7">
                <div style={{ height: '400px', width: '100%', borderRadius: '8px', overflow: 'hidden', border: '1px solid #ced4da', position: 'relative', zIndex: 1 }}>
                  <MapContainer
                    center={[position.lat, position.lng]}
                    zoom={13}
                    scrollWheelZoom={true}
                    style={{ height: '100%', width: '100%' }}
                  >
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <LocationPicker position={position} setPosition={setPosition} fetchReverseGeocode={fetchReverseGeocode} />
                    <UpdateMapCenter position={position} />
                    {position && radius > 0 && (
                      <Circle
                        center={position}
                        pathOptions={{ fillColor: '#28a745', color: '#28a745' }}
                        radius={radius}
                      />
                    )}
                  </MapContainer>
                </div>
                <small className="text-muted mt-2 d-block">
                  <i className="fa fa-info-circle me-1"></i>
                  Click anywhere on the map to set the geozone center location and automatically find the place name.
                </small>
              </Col>
            </Row>
          </Form>
        </CardBody>
      </Card>
    </>
  );
}

export default AddEditGeoZone;